import { useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementStyle, StripeErrorType } from '@stripe/stripe-js';

import { useMemberPaymentContext } from 'modules/common/components/Payment/MemberPaymentContext';
import config from 'modules/common/config';

const stripeElementStyle: StripeElementStyle = {
    base: {
        fontSize: '16px',
        color: '#000000',
        lineHeight: '24px',
        fontWeight: '100',
        fontFamily: "'Mier B', sans-serif",
        fontSmoothing: 'antialiased',
        '::placeholder': {
            color: 'rgba(0, 0, 0, 0.4)',
        },
    },
    invalid: {
        color: '#ED6C4C',
    },
};

const stripeElementOptions = { style: stripeElementStyle };

const getErrorMessage = (errorCode: StripeErrorType, msg?: string) => {
    switch (errorCode) {
        case 'card_error':
            return `There was a problem updating your payment method. ${msg} Please check your details and try again.`;
        default:
            return `There was a problem updating your payment method. Please try again in a few seconds. If it persists, please contact us: ${config.companyEmail}`;
    }
};

interface CardFormProps {
    setIsCreatingToken: (isCreatingToken: boolean) => void;
}

export default function CardForm({ setIsCreatingToken }: CardFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const { isPaymentModalOpen, hasPaymentIssue, setStripeTokenId, setStripeMessage } = useMemberPaymentContext();

    const isCardNumberOKRef = useRef(false);
    const isCardExpiryOKRef = useRef(false);
    const isCardCVCOKRef = useRef(false);
    const zipcodeRef = useRef('');
    const [zipcode, setZipcode] = useState('');
    const zipRef = useRef<HTMLInputElement | null>(null);

    const isModalActuallyOpen = isPaymentModalOpen || hasPaymentIssue;

    // only clear the form when the modal is actually closed
    // IMC can be opened but it shouldn't remove the info
    // because when IMC closes, the modal will be opened again (current behavior)
    useEffect(() => {
        if (isModalActuallyOpen) {
            return;
        }
        isCardNumberOKRef.current = false;
        isCardExpiryOKRef.current = false;
        isCardCVCOKRef.current = false;
        zipcodeRef.current = '';
        setZipcode('');
        elements?.getElement(CardNumberElement)?.clear();
        elements?.getElement(CardExpiryElement)?.clear();
        elements?.getElement(CardCvcElement)?.clear();
    }, [isModalActuallyOpen, elements]);

    const setZip = (next: string) => {
        zipcodeRef.current = next;
        setZipcode(next);
    };

    const handleZipValidation = (zip: string) => {
        if (!zip) {
            return;
        }
        const isValidZip = /^\d{5}(-\d{4})?$/.test(zip);
        if (isValidZip) {
            setStripeMessage({ firstTime: true, message: '' });
            zipRef.current?.setCustomValidity('');
        } else {
            setStripeMessage({
                isSuccess: false,
                firstTime: false,
                message: 'Please enter a valid 5-digit ZIP code.',
            });
            zipRef.current?.setCustomValidity('Please enter a valid 5-digit ZIP code.');
        }
    };

    const createTokenIfOK = () => {
        if (
            !(
                isCardNumberOKRef.current &&
                isCardExpiryOKRef.current &&
                isCardCVCOKRef.current &&
                zipRef.current?.checkValidity()
            )
        ) {
            setStripeTokenId('');
            return;
        }
        if (!(stripe && elements)) {
            return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
            return;
        }

        setIsCreatingToken(true);

        stripe
            .createToken(cardNumberElement, { address_zip: zipcodeRef.current })
            .then((payload) => {
                if (payload.error) {
                    // Clear any previously-tokenized card so the Update button can't
                    // submit a stale token after a subsequent failed tokenization.
                    setStripeTokenId('');
                    setStripeMessage({
                        isSuccess: false,
                        firstTime: false,
                        message: getErrorMessage(payload.error.type, payload.error.message),
                    });
                } else if (payload.token) {
                    setStripeMessage({ firstTime: true });
                    setStripeTokenId(payload.token.id);
                }
            })
            .catch(() => {
                // Network errors are surfaced by the Stripe SDK; clear any previously
                // captured token so a stale value can't be submitted.
                setStripeTokenId('');
            })
            .finally(() => {
                setIsCreatingToken(false);
            });
    };

    const handleNumberChange = (change: { complete: boolean; error?: { message: string } }) => {
        isCardNumberOKRef.current = change.complete;
        handleZipValidation(zipcodeRef.current);
        createTokenIfOK();
    };

    const handleExpiryChange = (change: { complete: boolean; error?: { message: string } }) => {
        isCardExpiryOKRef.current = change.complete;
        handleZipValidation(zipcodeRef.current);
        createTokenIfOK();
    };

    const handleCVCChange = (change: { complete: boolean; error?: { message: string } }) => {
        isCardCVCOKRef.current = change.complete;
        handleZipValidation(zipcodeRef.current);
        createTokenIfOK();
    };

    const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setZip(next);
        if (next.length === 5 || zipcode.length === 5) {
            handleZipValidation(next);
            createTokenIfOK();
        }
    };

    return (
        <Box
            sx={{
                '& .StripeElement': {
                    borderRadius: '8px',
                    lineHeight: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.500',
                    transition: 'border-color, background-color 0.2s ease-in-out',
                    padding: '11px 16px',
                    color: 'grey.900',
                    '&--focus': {
                        borderColor: 'black',
                        borderWidth: 2,
                    },
                    '&--invalid': {
                        borderColor: 'error.main',
                    },
                },
            }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Card Number
                </Typography>
                <CardNumberElement onChange={handleNumberChange} options={stripeElementOptions} />
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Exp. date
                    </Typography>
                    <CardExpiryElement onChange={handleExpiryChange} options={stripeElementOptions} />
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        CVC
                    </Typography>
                    <CardCvcElement onChange={handleCVCChange} options={stripeElementOptions} />
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        ZIP Code
                    </Typography>
                    <TextField
                        fullWidth
                        required
                        inputRef={zipRef}
                        inputProps={{
                            maxLength: 5,
                            'data-testid': 'zipcodeInput',
                        }}
                        value={zipcode}
                        variant="standard"
                        onChange={handleZipcodeChange}
                        onBlur={(e) => handleZipValidation(e.target.value)}
                        placeholder="90210"
                    />
                </Box>
            </Box>
        </Box>
    );
}
